import { Link } from "react-router-dom";
import { Chip, Skeleton } from "@heroui/react";
import { DirectionChip, Panel, Segmented } from "@/components/kit";
import { cn } from "@/lib/utils";
import { formatR } from "../chart/chartSignals";
import { utcHm } from "../daytrader/dayTrader";
import {
  ENTRY_TYPE_META, entryTypeOf, isTradeOpen,
  type EntryType, type SummaryPeriod, type TradeStats, type useMyTrades,
} from "./deskData";

const PERIODS: { id: SummaryPeriod; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
];
const SHORT_TYPE: Record<EntryType, string> = { signal: "Signal", early: "Early", own: "Own idea" };
/** Below this many closed trades a win rate is mostly luck. */
const SAMPLE_OK = 20;

/** Dollars with the sign first: −$0.10, not $-0.10. */
const usd = (n: number) => `${n < 0 ? "−" : ""}$${Math.abs(n).toFixed(2)}`;
/** Even = closed within ±0.05R: not a win, not a loss. */
const tally = (st: TradeStats) => `${st.won} won · ${st.lost} lost${st.even > 0 ? ` · ${st.even} even` : ""}`;

const rTone = (r: number) => (r > 0.05 ? "text-profit" : r < -0.05 ? "text-loss" : "text-foreground");

/**
 * The trader's own results: win rate and net R for the period, split by how
 * each trade was entered (signal / early / own idea), since comparing those is
 * what tells you whether to trust the engine, wait for it, or override it.
 */
export function TodayTrades({
  my, period, onPeriod, riskUsd, emptyHint = "Use Buy or Sell above the chart to track one (practice works too).", className,
}: {
  my: ReturnType<typeof useMyTrades>;
  period: SummaryPeriod;
  onPeriod: (p: SummaryPeriod) => void;
  /** Risk per trade in dollars: turns R into money. */
  riskUsd: number;
  /** Where to start a trade from, in this view. */
  emptyHint?: string;
  className?: string;
}) {
  const s = my.summary;
  const inPeriod = my.from == null ? [] : my.trades.filter(t => t.direction !== "WATCH" && t.created_at >= my.from! && !(t.status === "cancelled" && t.exit_price == null));
  const closed = s ? s.all.won + s.all.lost + s.all.even : 0;

  return (
    <Panel
      title="Your trades"
      description="Win rate and R, after fees"
      className={className}
      actions={<Segmented aria-label="Period" options={PERIODS} value={period} onChange={onPeriod} />}
    >
      {!s ? (
        my.error ? <p className="text-sm text-muted">Couldn't load your trades.</p> : <Skeleton className="h-28 rounded-lg" />
      ) : s.all.taken === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">
            No trades {period === "today" ? "today" : "in this period"} yet. {emptyHint}
          </p>
          <EngineLine st={s.bySource?.auto} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Trades" value={String(s.all.taken)} sub={s.all.open > 0 ? `${s.all.open} open` : "all closed"} />
            <Stat
              label="Win rate"
              value={s.all.winRate == null ? "—" : `${Math.round(s.all.winRate)}%`}
              sub={tally(s.all)}
            />
            <Stat
              label="Net"
              value={formatR(s.all.netR)}
              sub={`≈ ${usd(s.all.netR * riskUsd)}`}
              tone={rTone(s.all.netR)}
            />
          </div>

          <div>
            <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-muted">By how you entered</div>
            <ul className="divide-y divide-separator text-xs">
              {(Object.keys(ENTRY_TYPE_META) as EntryType[]).map(type => (
                <TypeRow key={type} label={ENTRY_TYPE_META[type].label} hint={ENTRY_TYPE_META[type].hint} st={s.byType[type]} />
              ))}
            </ul>
            <p className="mt-1 text-2xs text-muted">
              Real {s.byMode.real.taken} · Practice {s.byMode.paper.taken}
              {closed < SAMPLE_OK && ` · ${closed} closed so far: judge after ${SAMPLE_OK}+, fewer is mostly luck`}
            </p>
          </div>

          {inPeriod.length > 0 && (
            <ul className="space-y-1.5 text-xs">
              {inPeriod.slice(0, 6).map(t => {
                const open = isTradeOpen(t);
                const r = t.r_multiple;
                return (
                  <li key={t.id} className="flex items-center gap-2">
                    <span className="w-12 font-semibold">{t.symbol.replace("USDT", "")}</span>
                    <DirectionChip direction={t.direction === "SHORT" ? "SHORT" : "LONG"} />
                    <span className="text-muted">{SHORT_TYPE[entryTypeOf(t)]}</span>
                    {t.mode === "paper" && <Chip size="sm" variant="soft" className="text-3xs">practice</Chip>}
                    {t.rule_break && (
                      <span title={t.rule_break}><Chip size="sm" variant="soft" color="danger" className="text-3xs">broke limit</Chip></span>
                    )}
                    <span className={cn("num ml-auto", r != null && rTone(r))}>
                      {open ? `open ${r != null ? formatR(r) : ""}` : r != null ? formatR(r) : "closed"}
                    </span>
                    <span className="num w-10 text-right text-muted">{utcHm(t.created_at / 1000)}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <EngineLine st={s.bySource?.auto} />
          <Link to="/trades" className="inline-block text-xs text-brand-text underline underline-offset-2">All trades</Link>
        </div>
      )}
    </Panel>
  );
}

/** The engine's own signals in the same period (auto-tracked as practice), for comparison. */
function EngineLine({ st }: { st?: TradeStats }) {
  if (!st || st.taken === 0) return null;
  return (
    <p className="border-t border-separator pt-2 text-xs text-muted">
      <span className="font-medium text-foreground">Engine signals</span> (tracked automatically): {tally(st)}
      {st.winRate != null && <> · <span className="num">{Math.round(st.winRate)}%</span></>}
      {st.won + st.lost + st.even > 0 && <> · <span className={cn("num", rTone(st.netR))}>{formatR(st.netR)}</span></>}
      {st.open > 0 && <> · {st.open} open</>}
    </p>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-secondary px-2.5 py-2">
      <div className="text-2xs text-muted">{label}</div>
      <div className={cn("num text-base font-semibold", tone)}>{value}</div>
      <div className="text-2xs leading-tight text-muted">{sub}</div>
    </div>
  );
}

function TypeRow({ label, hint, st }: { label: string; hint: string; st: TradeStats }) {
  return (
    <li className="flex items-center gap-2 py-1.5" title={hint}>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {st.taken === 0 ? (
        <span className="text-muted">none</span>
      ) : (
        <>
          <span className="num text-muted">
            {st.won}W {st.lost}L{st.even > 0 && ` ${st.even} even`}{st.open > 0 && ` · ${st.open} open`}
          </span>
          <span className="num w-10 text-right">{st.winRate == null ? "—" : `${Math.round(st.winRate)}%`}</span>
          <span className={cn("num w-14 text-right font-medium", rTone(st.netR))}>{formatR(st.netR)}</span>
        </>
      )}
    </li>
  );
}
